// i18n area 'system' (task #127): sections explorer, tags, scopes, sysinfo, licensing, rbac, groups, users, credentials, proximity.
// Split out of the old monolithic dictionaries.ts by scripts/split-dictionaries.mjs;
// since the split, these files are the SOURCE — add and edit keys here. The script
// only re-runs to re-split a merged monolithic dictionaries.ts (see its header).

export const enUS = {
  diagnostics: {
    title: 'Diagnostics',
    subtitle: 'What is wrong, worst first — the same lens as tvirt diagnose',
    reload: 'Reload',
    loadError: 'The diagnosis could not be read',
    exportBundle: 'Export bundle',
    exportError: 'The bundle could not be minted',
    noProblem: 'No problem found in the inspected areas',
    nothingInspected: 'NOTHING was inspected — this is not a clean bill of health',
    inspected: 'Inspected',
    notInspected: 'NOT inspected',
    localHint: 'The node-local areas (systemd units, journal) are CLI-only: tvirt diagnose --area local — the path for when the API cannot answer.',
    thresholds: 'failed tasks within {hours} h, heartbeat late past {seconds} s',
    severity: {
      critical: 'Critical',
      warning: 'Warning',
      unknown: 'Unknown'
    }
  },
  explorer: {
    title: 'Resource explorer',
    root: 'Root',
    empty: 'Nothing lives in this scope',
    previewHint: 'Select an object to preview it',
    open: 'Open',

    // Il deep-link ?scope= delle liste CRUD (round #109), dall'esplora (#143).
    openScoped: 'Open the list in this scope',
    kinds: {
      vm: 'VM',
      vmTemplate: 'VM template',
      ct: 'CT',
      ctTemplate: 'CT template',
      app: 'App',
      overlayNetwork: 'Overlay network',
      networkService: 'Network service',
      fabric: 'Fabric',
      subnet: 'Subnet',
      storage: 'Storage pool',
      disk: 'Disk',
      catalogFile: 'Catalog file',
      ociRegistry: 'OCI registry',
      ociImage: 'OCI image',
      cephCluster: 'Ceph cluster',
      backupDestination: 'Backup destination',
      user: 'User',
      group: 'Group',
      role: 'Role',
      credential: 'Credential',
      tag: 'Tag',
      proximityRule: 'Proximity rule'
    }
  },
  tags: {
    title: 'Tags',
    empty: 'No tags',
    add: 'Add a tag',
    remove: 'Remove',
    name: 'Tag',
    value: 'Value',
    save: 'Save',
    saveError: 'Could not save the tags',
    loadError: 'Could not load the tag vocabulary',
    noVocabulary: 'No tag is available for this kind of resource',

    // A single-valued tag already set answers 409: the replacement is always
    // explicit — never an implicit substitution (design, ratified 2026-07-27).
    overwrite: 'Replace the current value',

    // Sistema > Tag, the vocabulary surface.
    createTitle: 'Create tag',
    editTitle: 'Edit tag',
    nameHint: 'Letters, digits, dots, dashes and underscores — no ":" and no "/"; the tvirt- prefix belongs to the platform',
    cardinality: 'Cardinality',
    cardinalitySingle: 'One value',
    cardinalityMultiple: 'Several values',
    cardinalityHint: 'With one value, re-tagging a resource asks to replace it',
    autoCreate: 'Auto-create values',
    autoCreateOn: 'On assignment',
    autoCreateOff: 'Declared only',
    autoCreateHint: 'On: assigning a new value creates it. Off: only the declared values may be assigned',
    values: 'Values',
    noValues: 'No value declared',
    addValue: 'Add value',
    duplicateValue: 'Duplicate value',
    kinds: 'Resource kinds',
    kindsAll: 'All',
    kindsPlaceholder: 'All',
    kindsHint: 'Leave empty to allow every taggable kind',
    systemChip: 'System',

    // Parity R1 (2026-11-03): the delete confirm's `cascade` option — the
    // twin of the CLI's `--cascade` (contract deleteTag).
    deleteCascade: 'Also remove the assignments',
    deleteCascadeHint: 'A tag still assigned to some resource is refused: with this option its values and every assignment go away together with the tag.'
  },
  scopes: {
    title: 'Scopes',
    createTitle: 'Create scope',
    editTitle: 'Rename scope',
    path: 'Path',
    segment: 'Segment',
    segmentHint: 'Lowercase letters, digits and dashes (DNS-1123)',
    parent: 'Parent',
    rootChip: 'Root',
    preview: 'New scope',
    pickLabel: 'Scope',
    pickHint: 'Where the object lives in the cluster tree — the root or a vDC under it.',
    ociCache: 'OCI cache (override)',
    appEphemeral: 'App ephemeral pool (override)',
    inherit: 'Inherit',
    inheritsFrom: 'Inherits from',
    inheritsCluster: 'Inherits the cluster default',
    inheritsNone: 'Inherits — no effective pool',
    failoverProtection: 'Protection in degraded cluster',
    networkWeight: 'Network weight',
    networkCeil: 'Network ceiling (Mbps)',
    protectionProtected: 'Protected',
    protectionExpendable: 'Expendable when degraded',
    protectionDefault: 'Cluster default'
  },

  // Sistema > Informazioni (round #124, 2026-07-31): platform versions across
  // the fleet (at most two mid-upgrade), per-node component versions, the
  // locally served manual PDF and the absorbed license card.
  sysinfo: {
    summary: 'Platform versions across the cluster, the components each node runs, the administration manual and the license.',
    platformTitle: 'TVirt platform',
    platformSubtitle: 'Versions observed on the cluster nodes',
    versionOnNodes: '{version} on {n} nodes',
    versionOnOneNode: '{version} on 1 node',
    mixedVersions: 'More than one platform version in the cluster — expected only while an upgrade is in progress (at most two).',
    tooManyVersions: 'More than two platform versions in the cluster — never expected, not even mid-upgrade: check the nodes below.',
    unreportedNodes: 'No versions report yet from: {nodes}',
    noVersionsYet: 'No node has reported its versions yet.',
    nodesUnavailable: 'The node list is not visible with your permissions — platform versions cannot be shown.',
    colNode: 'Node',
    manualTitle: 'Administration manual',
    manualSubtitle: 'Local copy, served by this cluster',
    manualBody: 'The full TVirt manual as a PDF, served directly by the platform — available in air-gapped environments too.',
    manualLink: 'Administration manual (PDF)',
    apiExplorerTitle: 'API explorer',
    apiExplorerSubtitle: 'The OpenAPI contract, browsable',
    apiExplorerBody: 'The platform\'s complete OpenAPI contract — every route, schema and response code — browsable from the browser, served through this node.',
    apiExplorerLink: 'API Explorer',
    kubeTitle: 'Kubernetes compatibility',
    kubeSubtitle: 'kubectl and its kin, in front of the native API',
    kubeBody: 'TVirt KubeCompat projects Pods, Deployments, Services, namespaces, nodes and events in the Kubernetes dialect — read-only, plus deleting Deployments. Every call goes through the native API with your permissions: in the kubeconfig, use an app token as the bearer.',
    kubeEndpoint: 'Endpoint for kubectl',
    kubeStatusActive: 'Active — Kubernetes {version}',
    kubeStatusMissing: 'Not installed on this node (package trantor-tvirt-kubecompat)',
    kubeStatusDown: 'Installed but not answering — check the trantor-tvirt-kubecompat unit',
    kubeStatusChecking: 'Checking…'
  },

  // Licensing (round 2026-07-29): the cluster's subscription license page.
  licensing: {
    title: 'Cluster license',
    subtitle: 'Subscription license — at most one per cluster',
    absentTitle: 'No license registered',
    absentBody:
      'Without a valid license the cluster refuses to start new virtual machines (running VMs are untouched). Register the product key that came with your subscription.',
    registerButton: 'Register license',
    removeButton: 'Remove license',
    state: {
      ok: 'Valid',
      limitExceeded: 'Limit exceeded',
      expired: 'Expired'
    },
    productKeyLabel: 'Product key',
    basisLabel: 'Limit basis',
    basis: {
      concurrentVms: 'Concurrent powered-on VMs',
      physicalCores: 'Physical cores (cluster total)'
    },
    limitLabel: 'Limit',
    usageLabel: 'Current usage',
    usageOf: '{used} of {limit}',
    usagePartial: 'partial sum — {n} node(s) without an observed CPU topology',
    usageUnknown: 'not observable right now',
    expiresLabel: 'Expires',
    neverExpires: 'never',
    registeredLabel: 'Registered',
    gateNote:
      'With the license absent, expired or beyond its limit the cluster refuses to start NEW virtual machines. Running VMs stay up and HA restarts are unaffected.',
    devStage:
      'Development stage: the product key is accepted as-is together with the limits declared here; signed-key validation arrives in a future release.',
    dialogTitle: 'Register license',
    dialogExpiresHelp: 'Empty = the license never expires',
    dialogSubmit: 'Register',
    dialogError: 'Could not register the license.',
    removeTitle: 'Remove license',
    removeBody:
      'The cluster will run UNLICENSED: new virtual machines will refuse to start until a license is registered. Running VMs are untouched.',
    removeConfirm: 'Remove',
    removeError: 'Could not remove the license.'
  },
  rbac: {
    tabRoles: 'Roles',
    tabGrants: 'Grants',
    rolesTitle: 'Roles',
    createRoleTitle: 'Create role',
    editRoleTitle: 'Edit role',
    nameHint: 'Lowercase letters, digits and dashes; the tvirt- prefix is reserved',
    systemChip: 'System',
    permissions: 'Permissions',
    permissionsPlaceholder: 'Add permission…',
    includedRoles: 'Included roles',
    includedRolesPlaceholder: 'Add role…',
    grantsTitle: 'Grants',
    createGrantTitle: 'Create grant',
    principal: 'Principal',
    principalKind: 'Principal kind',
    principalUser: 'User',
    principalGroup: 'Group',
    role: 'Role',
    grantRight: 'Delegation',
    grantRightChip: 'Can delegate',
    grantRightLabel: 'With grant right (the grantee can delegate this role further)',
    effective: 'Effective',
    notEffective: 'Not effective',
    createdAt: 'Created',
    scopeHint: '/path exact, /path/* recursive — grants anchor at a mounted root (/* is not grantable)'
  },
  groups: {
    title: 'Groups',
    createTitle: 'Create group',
    nameHint: 'Lowercase letters, digits and dashes; unique within the scope',
    members: 'Members',
    membersTitle: 'Members',
    membersPlaceholder: 'Add user…',
    noMembers: 'No members',
    realmMappings: 'Realm mappings',
    realmMappingsTitle: 'Realm mappings',
    realmMappingsHint:
      'What this group corresponds to in each external realm. Membership is read from the directory at sign-in: a directory group nobody maps here is ignored, and nesting is not resolved — flatten it directory-side.',
    realm: 'Realm',
    externalGroup: 'External group',
    addRealmMapping: 'Add mapping',
    removeRealmMapping: 'Remove mapping',
    noRealmMappings: 'No realm mappings',

    // Sectioned create dialog (realm-wizard round, 2026-08-03): General plus
    // the external-realm mapping surface, with its speaking empty state.
    sectionGeneral: 'General',
    mappingsNoRealms: 'No external realm configured yet — the mapping surface unlocks once a realm exists.',
    mappingsNoRealmsLink: 'Open the Realms & SSO tab',

    // Group-imposed MFA obligation (round #178 half B): a pure OR — a group
    // can only ADD the obligation, never lift it.
    mfaRequired: 'MFA obligation',
    mfaRequiredChip: 'Imposes MFA',
    mfaNotRequiredChip: 'No obligation',
    mfaRequiredHint:
      'A pure OR: a group can ADD the second-factor obligation to its members — external identities too, through its realm mappings — never lift it. It applies from the next login: open sessions are not closed.'
  },
  users: {
    title: 'Users',
    createTitle: 'Create user',
    editTitle: 'Edit user',
    username: 'Username',
    usernameHint: 'Lowercase letters, digits, dots and dashes',
    displayName: 'Display name',
    enabled: 'Enabled',
    state: 'State',
    enabledChip: 'Enabled',
    disabledChip: 'Disabled',
    lockedChip: 'Locked',
    password: 'Password',
    passwordSetChip: 'Set',
    mustChangeChip: 'Must change it',
    initialPassword: 'Initial password',
    initialPasswordHint: 'The user must change it at first login',
    lastLogin: 'Last login',
    neverLoggedIn: 'Never logged in',
    setPasswordTitle: 'Set password',
    setPasswordHint: 'Administrative replace — it requires your password (sudo mode) and forces the user to change it at the next login.',
    operatorPassword: 'Your password',
    unlock: 'Unlock the account',
    revokeTitle: 'Revoke sessions',
    revokeBody: 'Every active session of this user is terminated immediately.',
    revokeIncludeTokens: 'Also revoke the app tokens',
    revokeConfirm: 'Revoke',

    // Dialog standard (round #156): complete details, tabbed account/security.
    tabAccount: 'Account',
    tabSecurity: 'Security',
    usernameImmutableHint: "The username is the account's identity — fixed at creation",
    groups: 'Groups',
    noGroups: 'No groups',
    mustChangePassword: 'Password change required',
    failedAttempts: 'Failed login attempts',
    lockedUntil: 'Locked until',

    // Identity tabs (2026-08-03): Sistema > Utenti became a tabbed section on
    // the /system/rbac model — users, groups, external realms.
    tabUsers: 'Users',
    tabGroups: 'Groups',
    tabRealms: 'Realms & SSO',
    tabMfa: 'MFA',

    // MFA tab (task #178): the second-factor view. Half A — the per-user
    // table on users.yaml; half B (contract round, ratified 2026-08-04) — the
    // effective verdict with its provenance, the external enrollments with
    // the orphan state, and the groups that impose the obligation.
    mfa: {
      title: 'Second factor (MFA)',
      methodsTitle: 'Available methods',
      methodsSubtitle: 'What this deployment can verify as a second factor',
      methodsError: 'Could not load the method registry',
      builtinChip: 'Built-in',
      enrollableChip: 'Enrollment in TVirt',
      delegatedChip: 'Managed externally',
      state: 'State',
      stateActive: 'Active',
      statePending: 'Awaiting enrollment',
      stateNone: 'Not enrolled',
      required: 'MFA obligation',
      requiredChip: 'Required',
      notRequiredChip: 'Not required',
      enrolledAt: 'Enrolled on',
      enrolledMethods: 'Enrolled methods',
      noMethods: 'No enrollment',
      codesRemaining: 'Recovery codes left',
      resetTitle: 'Reset the second factor',
      resetHint:
        'The enrollment and every recovery code are wiped: at the next login the user enrolls again from scratch. The MFA obligation stays as it is — an administrator never sets a secret, they only clear it.',
      resetConfirm: 'Reset',
      resetError: 'Could not reset the second factor',
      empty: 'No managed user yet — accounts appear here as soon as they exist, each with its second-factor state.',

      // Half B (round #178): provenance of the effective verdict, external
      // enrollments with the orphan state, groups that impose the obligation.
      viaSelf: 'Personal',
      viaSelfHint: "The account's own contribution (its flag) — the only part the pencil edits.",
      viaGroupHint: 'Obligation inherited from this group: removing the personal flag does not free the user — act on the group, in the Groups tab.',
      subject: 'Identity',
      lastSeenAt: 'Last confirmed',
      enrollmentPending: 'Pending',
      orphanChip: 'Orphaned',
      orphanDirectoryMiss: 'The identity is no longer in the directory',
      orphanRealmRemoved: 'The realm was removed from TVirt',
      orphanedAt: 'Orphaned since',
      directoryUuid: 'UUID in the directory',
      externalTitle: 'External enrollments',
      externalEmpty: 'No external enrollment yet — one is born at the first login of a directory identity under the MFA obligation.',
      orphanHelp:
        'An orphaned enrollment is never deleted on its own: it is security material — it stays for the audit and, if the identity reappears in the directory, it finds its factor again. The only way out is the administrative reset.',
      visibilityHint: 'External enrollments reside at the cluster root: only who holds user.read over the whole tree sees them.',
      orphanResetHint:
        'The orphaned row is removed for good: nobody claims it any more. If the identity ever comes back to the directory, it will enroll again from scratch.',
      enforcingTitle: 'Groups that impose MFA',
      enforcingSubtitle:
        'The anchor of the obligation for external identities is the group: its realm mappings say which directory population it reaches. The external enrollment is born at the first login of the obligated user.',
      enforcingEmpty: 'No group imposes MFA — the obligation is switched on on the group, from the Groups tab.',
      enforcingError: 'Could not load the groups that impose MFA'
    }
  },
  realms: {
    title: 'Authentication realms',
    createTitle: 'Create realm',
    editTitle: 'Edit realm',
    name: 'Realm',
    nameHint: 'The domain users type after the @ (example.com) — immutable after creation',
    nameImmutableHint: 'The realm name is stamped in every session and every audit line that ever named it — renaming would rewrite history',
    kind: 'Kind',
    kindLdap: 'LDAP / Active Directory',
    kindHint: 'OIDC and SAML arrive with the next rounds — today a realm is an LDAPv3 directory',

    // Creation semi-wizard (realm-wizard round, 2026-08-03): the kind cards of
    // step 1 — ldap live, oidc/saml as roadmap cards (never a form without a
    // backend behind it).
    kindCategory: 'Realm kind',
    kindSelectHint: 'Select the kind of directory this realm will authenticate against.',
    kindCard: {
      ldap: {
        name: 'LDAP / Active Directory',
        desc: 'Username and password verified with a bind on an LDAPv3 directory — Active Directory, FreeIPA, OpenLDAP.'
      },
      oidc: {
        name: 'OpenID Connect',
        desc: 'Browser single sign-on delegated to an OpenID Connect identity provider — arrives with the next rounds.'
      },
      saml: {
        name: 'SAML 2.0',
        desc: 'Enterprise single sign-on through SAML 2.0 assertions — arrives with the next rounds.'
      }
    },
    tabBind: 'Bind',
    enabled: 'Enabled',
    urls: 'Directory servers',
    urlsHint: 'One ldap:// or ldaps:// URL per line (1–8), tried in order — failover, never balancing',
    startTls: 'StartTLS',
    verifyCertificate: 'Verify the certificate',
    cleartextWarning: 'Without ldaps:// or StartTLS the bind password crosses the wire in the clear — acceptable for a lab, never for production.',
    noVerifyWarning: 'An unverified TLS session authenticates nobody: turn verification off only against a directory you cannot fix.',
    caCertificate: 'CA certificate (PEM)',
    caCertificateHint: 'Optional CA bundle pinned for this realm’s TLS/StartTLS handshake; empty clears it',
    caCertificatePinned: 'Pinned',
    connectTimeout: 'Timeout (seconds)',
    connectTimeoutHint: 'Per-URL connect and request deadline (1–60); on expiry the next URL is tried',
    bindDn: 'Bind DN',
    bindDnHint: 'The service account that runs the user search; empty = anonymous search bind (refused by AD)',
    anonymousBind: 'Anonymous search bind',
    bindCredential: 'Bind password (keystore)',
    bindCredentialHint: 'A usernamePassword keystore entry holding the bind password — the secret never lives on the realm',
    bindCredentialNone: 'None (anonymous bind)',
    bindCredentialEmpty: 'No usernamePassword entry in the keystore yet — create one under System > Credentials to hold the bind password.',
    baseDn: 'Base DN',
    baseDnHint: 'The subtree the user search starts from (DC=example,DC=com)',
    userFilter: 'User filter',
    userFilterHint: '%u is replaced by the login’s local part, RFC 4515-escaped; non-AD directories typically want (&(objectClass=inetOrgPerson)(uid=%u))',
    memberOfAttribute: 'memberOf attribute',
    memberOfAttributeHint: 'Read once, at login — unmapped groups are ignored, nesting is not resolved',
    uuidAttribute: 'UUID attribute',
    uuidAttributeHint: 'The immutable per-realm UUID (objectGUID on AD, entryUUID on OpenLDAP) — stamped in the audit beside user@realm',
    displayNameAttribute: 'Display name attribute',
    displayNameAttributeHint: 'Used to greet the user; falls back to the login name',
    sudoHint: 'Realm writes are sudo-protected: a realm is a door into the platform',
    deleteBody: 'The realm’s group mappings go with it; the sessions it authenticated stay alive until they expire — a deleted realm simply admits nobody new.',
    tabConnection: 'Connection',
    tabDirectory: 'Directory',
    testTitle: 'Test connection',
    testHint: 'A configuration probe, never an authentication: it connects, binds as the service account and — with a username — runs the user filter.',
    testUsername: 'Username (optional)',
    testUsernameHint: 'A local part to run the user filter against — 1 match is the healthy answer',
    testRun: 'Run test',
    testError: 'The probe could not be run',
    testReachable: 'Directory reachable, service bind accepted',
    testUnreachable: 'Directory NOT reachable',
    matchedOne: 'The user filter matched exactly 1 entry — the healthy answer.',
    matchedNone: 'The user filter matched no entry: the filter or the base DN is wrong.',
    matchedMany: 'The user filter matched {count} entries: it is not selective, and logins would be ambiguous (refused).',
    mappingsPointer: 'What an external group corresponds to is a property of the TVirt group (its realm mappings): the point of truth lives in the Groups tab.',
    mappingsPointerLink: 'Open the Groups tab',
    rootHomeHint: 'The identities of an external realm live at the cluster root, never in a vDC: permissions are assigned where you need them, with grants — on the group or directly on the user, exactly as for natives.'
  },
  credentials: {
    title: 'Credentials',
    createTitle: 'Create credential',
    editTitle: 'Edit credential',
    kind: 'Kind',
    kindUsernamePassword: 'Username and password',
    kindIscsiChap: 'CHAP (iSCSI)',
    kindNvmeDhchap: 'DH-HMAC-CHAP (NVMe)',
    kindCephx: 'cephx (Ceph identity)',
    kindHintUsernamePassword: 'Immutable. For CIFS/SMB storages — the mount account whose secret the worker uses at mount time.',
    kindHintIscsiChap: 'Immutable. For iSCSI CHAP authentication — username = CHAP name, secret = CHAP secret.',
    kindHintNvmeDhchap: 'Immutable. For NVMe over Fabrics DH-HMAC-CHAP — secret = the DHHC-1:xx:… blob; the username is just a label.',
    kindHintCephx: 'Immutable. For Ceph clients — username = client name (client.…), secret = the base64 key.',
    kindImmutableHint: 'The kind is immutable after creation — retyping means a new credential.',
    username: 'Username',
    usernameChap: 'CHAP name',
    usernameNvme: 'Label',
    usernameCephx: 'Client name',
    usernameHint: 'Optional account name the secret belongs to',
    usernameHintChap: 'The CHAP name the initiator authenticates with',
    usernameHintNvme: 'Unused by the transport — a free label for your bookkeeping',
    usernameHintCephx: 'The cephx client name — the client. prefix is tolerated',
    secret: 'Secret',
    secretHint: 'Write-only: stored for the consumers, never shown again.',
    secretHintNvmeDhchap: 'Write-only: the host DH-HMAC-CHAP secret in its canonical DHHC-1:xx:… form — stored for the consumers, never shown again.',
    secretHintCephx: 'Write-only: the base64 cephx key — stored for the consumers, never shown again.',
    secretEditHint: 'Leave empty to keep the current secret — filling it rotates it.',
    createdAt: 'Created',
    inUseError: "'{name}' is referenced by a storage's configuration — detach it there first.",
    nameHint: 'Lowercase letters, digits and dashes (DNS-1123); unique per scope',

    // Rotation-only dialog (round #156): the secret never gets an inline pencil.
    rotateTitle: 'Rotate secret',
    rotateHint: 'Write-only: the new secret replaces the stored one — consumers pick it up at their next mount pass.',

    // Round #159 (PKI): the Certificates and CAs tabs.
    tabSecrets: 'Secrets',
    tabCertificates: 'Certificates',
    tabAuthorities: 'Certification authorities',
    certificatesTitle: 'Certificates',
    authoritiesTitle: 'Certification authorities',
    caCreateTitle: 'New CA',
    certCreateTitle: 'New certificate',
    caMode: 'Source',
    caModeGenerate: 'Generate with TVirt',
    caModeImport: 'Import (key + certificate)',
    certMode: 'Source',
    certModeIssue: 'Issue from a keystore CA',
    certModeImport: 'Import (key + certificate)',
    issuingCa: 'Issuing CA',
    issuingCaHint: 'The certificate lands in the CA’s scope, parent recorded',
    noCasHint: 'No CA in the keystore yet — generate or import one in the CAs tab first',
    commonName: 'Common name (CN)',
    commonNameCaHint: 'The CA subject: letters, digits, space, dot, underscore, dash',
    commonNameCertHint: 'OpenVPN identifies clients by CN — keep it unique per CA',
    days: 'Validity (days)',
    daysCaHint: '1–7300 days; 3650 (10 years) is the default',
    daysCertHint: '1–3650 days; 365 is the default',
    keyType: 'Key type',
    keyTypeHint: 'EC P-256 is the default; RSA for legacy consumers',
    usage: 'Usage',
    usageClient: 'Client (clientAuth)',
    usageServer: 'Server (serverAuth)',
    usageHint: 'The extended key usage the certificate is minted for',
    san: 'Alternative names (SAN)',
    sanHint: 'Comma-separated; IP literals become IP: entries, the rest DNS:',
    keyPem: 'Private key (PEM)',
    keyPemHint: 'Write-only: stored in the keystore, never shown again',
    certPem: 'Certificate (PEM)',
    certPemHint: 'The public certificate that pairs with the key',
    certPemCaHint: 'The CA certificate that pairs with the key (basicConstraints CA)',
    subject: 'Subject',
    issuer: 'Issuer',
    parentCa: 'Issuing CA',
    parentCaImported: 'Imported — issued outside TVirt',
    serial: 'Serial',
    fingerprint: 'Fingerprint (SHA-256)',
    validFrom: 'Valid from',
    validUntil: 'Valid until',
    expiry: 'Expiry',
    expired: 'Expired',
    expiresInDays: 'Expires in {days} d',
    revoked: 'Revoked',
    revokedAtLabel: 'Revoked at',
    revokeTitle: 'Revoke certificate',
    revokeAction: 'Revoke',
    revokeWarning: 'Revocation is terminal: the serial joins the issuing CA’s CRL and every consumer refusing revoked certificates (OpenVPN first) will reject this one. There is no un-revoke.',
    exportPem: 'Export PEM',
    exportChain: 'Export chain bundle',
    exportCrl: 'Export CRL',
    materialImmutableHint: 'The X.509 material is immutable — re-issue or re-import instead',
    caInUseError: "'{name}' still has live issued certificates (or a storage references it) — revoke or delete them first."
  },
  proximity: {
    title: 'TVirt Proximity',
    subtitle: 'Placement, availability and affinity — where clustered VMs run and whether they should be running',
    loadError: 'The Proximity panel needs the cluster store — fleet state is unreachable right now',
    config: {
      title: 'Configuration',
      subtitle: 'Cluster-wide placement policy',
      save: 'Save',
      saveError: 'The configuration was refused',
      strategy: 'Selection strategy',
      strategy_leastUsedResources: 'Least used resources',
      strategy_leastVms: 'Fewest VMs',
      strategy_random: 'Random',
      strategyLeastUsedResourcesHint: 'Among the valid candidates, the node with the most usable RAM left (observed total − host reservation − live VMs), vCPU commitment as tie-break. Deterministic for equal data.',
      strategyLeastVmsHint: 'Among the valid candidates, the node running the fewest live VMs; ties resolve by node name. Deterministic for equal data.',
      strategyRandomHint: 'Uniformly random among the valid candidates.',
      liveness: 'Node liveness threshold (seconds)',
      livenessHint: 'A member silent beyond this is unreachable: excluded from placement, and — unless in maintenance — treated as dead by the HA decider, which fences it and restarts its HA VMs elsewhere.',
      decider: 'HA decider',
      deciderNone: 'not elected yet'
    },
    nodesTitle: 'Nodes',
    nodesSubtitle: 'Liveness and empirical capacity — usable RAM = total − base − committed',
    nodeCol: 'Node',
    stateCol: 'State',
    ramCol: 'RAM (total − base − committed)',
    usableCol: 'Usable',
    vmsCol: 'Load',
    neverSeen: 'never seen',
    freshChip: 'reachable',
    staleChip: 'unreachable',
    attestedChip: 'attested down',
    ramFormula: 'total − base − committed',
    haTitle: 'High availability',
    haSubtitle: 'HA-flagged VMs are "always on": TVirt restarts them after any stop that was not asked for — same node with backoff, another node when theirs dies (shared storage only).',
    haNone: 'No VM carries the HA flag yet — enable it here or in the VM detail.',
    haVmCol: 'Virtual machine',
    haStateCol: 'State',
    haRestartsCol: 'HA restarts',
    haLastEventCol: 'Last event',
    haToggleCol: 'HA',
    haCrashLoop: 'crash loop',
    haToggleError: 'The HA change was refused',
    rules: {
      title: 'Affinity rules',
      createTitle: 'New proximity rule',
      editTitle: 'Edit rule',
      nameHint: 'Lowercase letters, digits and dashes (DNS label)',
      vms: 'Virtual machines',
      vmsHint: 'At least two VMs — the rule binds where they run relative to each other',
      type: 'Type',
      affinity: 'Affinity (together)',
      antiAffinity: 'Anti-affinity (apart)',
      typeAffinityHint: 'The VMs should run on the SAME node.',
      typeAntiAffinityHint: 'The VMs should run on DIFFERENT nodes — the classic HA pair.',
      mode: 'Mode',
      hard: 'hard',
      soft: 'soft',
      modeHardHint: 'Violations refuse the operation (start, migration, failover) with a speaking error.',
      modeSoftHint: 'Preferred, never blocking: a violated placement proceeds with a visible warning.',
      satisfied: 'Placement',
      satisfiedYes: 'satisfied',
      satisfiedNo: 'violated',

      // Dialog standard (round #156): the violations get a full row, not a tooltip.
      violations: 'Violations',
      noViolations: 'None'
    },
    events: {
      title: 'HA events',
      subtitle: 'The availability journal — restarts, fencing, failovers and the honest skips',
      none: 'Nothing yet.',
      when: 'When',
      kind: 'Event',
      target: 'Target',
      detail: 'Detail',
      kindRestarted: 'HA restart',
      kindGaveUp: 'gave up (crash loop)',
      kindNodeAttested: 'node fenced',
      kindFailedOver: 'failed over',
      kindNoCandidate: 'no candidate',
      kindStorageLocalSkip: 'skipped (local storage)',
      kindZombieFenced: 'zombie terminated'
    }
  }
}

export const itIT: typeof enUS = {
  diagnostics: {
    title: 'Diagnostica',
    subtitle: 'Cosa non va, prima il peggio — la stessa lente di tvirt diagnose',
    reload: 'Ricarica',
    loadError: 'Impossibile leggere la diagnosi',
    exportBundle: 'Esporta bundle',
    exportError: 'Impossibile coniare il bundle',
    noProblem: 'Nessun problema nelle aree ispezionate',
    nothingInspected: 'NULLA è stato ispezionato — non è un certificato di buona salute',
    inspected: 'Ispezionate',
    notInspected: 'NON ispezionate',
    localHint: "Le aree locali del nodo (unit systemd, journal) si leggono solo dalla CLI: tvirt diagnose --area local — è la via per quando l'API non risponde.",
    thresholds: 'task falliti entro {hours} h, heartbeat in ritardo oltre {seconds} s',
    severity: {
      critical: 'Critico',
      warning: 'Avviso',
      unknown: 'Sconosciuto'
    }
  },
  explorer: {
    title: 'Esplora risorse',
    root: 'Radice',
    empty: 'Nessuna risorsa in questo scope',
    previewHint: 'Seleziona un oggetto per vederne l\u2019anteprima',
    open: 'Apri',

    // Il deep-link ?scope= delle liste CRUD (round #109), dall'esplora (#143).
    openScoped: 'Apri l\u2019elenco in questo scope',
    kinds: {
      vm: 'VM',
      vmTemplate: 'Template VM',
      ct: 'CT',
      ctTemplate: 'Template CT',
      app: 'App',
      overlayNetwork: 'Rete overlay',
      networkService: 'Servizio di rete',
      fabric: 'Fabric',
      subnet: 'Subnet',
      storage: 'Pool di storage',
      disk: 'Disco',
      catalogFile: 'File di catalogo',
      ociRegistry: 'Registro OCI',
      ociImage: 'Immagine OCI',
      cephCluster: 'Cluster Ceph',
      backupDestination: 'Destinazione di backup',
      user: 'Utente',
      group: 'Gruppo',
      role: 'Ruolo',
      credential: 'Credenziale',
      tag: 'Tag',
      proximityRule: 'Regola di prossimit\u00e0'
    }
  },
  tags: {
    title: 'Tag',
    empty: 'Nessun tag',
    add: 'Aggiungi tag',
    remove: 'Rimuovi',
    name: 'Tag',
    value: 'Valore',
    save: 'Salva',
    saveError: 'Impossibile salvare i tag',
    loadError: 'Impossibile caricare il vocabolario dei tag',
    noVocabulary: 'Nessun tag disponibile per questo tipo di risorsa',

    // A single-valued tag already set answers 409: the replacement is always
    // explicit — never an implicit substitution (design, ratified 2026-07-27).
    overwrite: 'Sostituisci il valore attuale',

    // Sistema > Tag, the vocabulary surface.
    createTitle: 'Crea tag',
    editTitle: 'Modifica tag',
    nameHint: 'Lettere, cifre, punti, trattini e underscore \u2014 niente ":" n\u00e9 "/"; il prefisso tvirt- \u00e8 riservato alla piattaforma',
    cardinality: 'Cardinalit\u00e0',
    cardinalitySingle: 'Un solo valore',
    cardinalityMultiple: 'Pi\u00f9 valori',
    cardinalityHint: 'Con un solo valore, ri-assegnare il tag chiede di sostituirlo',
    autoCreate: 'Auto-creazione valori',
    autoCreateOn: 'All\u2019assegnazione',
    autoCreateOff: 'Solo dichiarati',
    autoCreateHint: 'Attiva: assegnare un valore nuovo lo crea. Disattiva: si assegnano solo i valori dichiarati',
    values: 'Valori',
    noValues: 'Nessun valore dichiarato',
    addValue: 'Aggiungi valore',
    duplicateValue: 'Valore duplicato',
    kinds: 'Tipi di risorsa',
    kindsAll: 'Tutti',
    kindsPlaceholder: 'Tutti',
    kindsHint: 'Lascia vuoto per ammettere ogni tipo di risorsa taggabile',
    systemChip: 'Sistema',

    // Parità R1 (2026-11-03): l'opzione `cascade` del confirm di eliminazione —
    // il gemello del `--cascade` della CLI (contract deleteTag).
    deleteCascade: 'Rimuovi anche le assegnazioni',
    deleteCascadeHint: 'Un tag ancora assegnato a qualche risorsa viene rifiutato: con questa opzione i valori e tutte le assegnazioni se ne vanno insieme al tag.'
  },
  scopes: {
    title: 'Scope',
    createTitle: 'Crea scope',
    editTitle: 'Rinomina scope',
    path: 'Percorso',
    segment: 'Segmento',
    segmentHint: 'Minuscole, cifre e trattini (DNS-1123)',
    parent: 'Genitore',
    rootChip: 'Radice',
    preview: 'Nuovo scope',
    pickLabel: 'Scope',
    pickHint: 'Dove vive l\'oggetto nell\'albero del cluster — la radice o un vDC al suo interno.',
    ociCache: 'Cache OCI (override)',
    appEphemeral: 'Pool effimeri App (override)',
    inherit: 'Eredita',
    inheritsFrom: 'Eredita da',
    inheritsCluster: 'Eredita il default di cluster',
    inheritsNone: 'Eredita — nessun pool effettivo',
    failoverProtection: 'Protezione in degrado',
    networkWeight: 'Peso di rete',
    networkCeil: 'Tetto di rete (Mbps)',
    protectionProtected: 'Protetta',
    protectionExpendable: 'Sacrificabile in degrado',
    protectionDefault: 'Default di cluster'
  },

  // Sistema > Informazioni (round #124, 2026-07-31): versioni piattaforma
  // nella flotta (al massimo due mid-upgrade), componenti per nodo, manuale
  // PDF servito localmente e card licenza inglobata.
  sysinfo: {
    summary: 'Versioni della piattaforma nel cluster, componenti di ogni nodo, manuale di amministrazione e licenza.',
    platformTitle: 'Piattaforma TVirt',
    platformSubtitle: 'Versioni osservate sui nodi del cluster',
    versionOnNodes: '{version} su {n} nodi',
    versionOnOneNode: '{version} su 1 nodo',
    mixedVersions: 'Più di una versione della piattaforma nel cluster — atteso solo durante un aggiornamento in corso (al massimo due).',
    tooManyVersions: 'Più di due versioni della piattaforma nel cluster — non è mai atteso, nemmeno durante un aggiornamento: controlla i nodi qui sotto.',
    unreportedNodes: 'Ancora nessun report versioni da: {nodes}',
    noVersionsYet: 'Nessun nodo ha ancora riportato le proprie versioni.',
    nodesUnavailable: 'L\'elenco dei nodi non è visibile coi tuoi permessi — impossibile mostrare le versioni della piattaforma.',
    colNode: 'Nodo',
    manualTitle: 'Manuale di amministrazione',
    manualSubtitle: 'Copia locale, servita da questo cluster',
    manualBody: 'Il manuale TVirt completo in PDF, servito direttamente dalla piattaforma — disponibile anche in ambienti air-gapped.',
    manualLink: 'Manuale di amministrazione (PDF)',
    apiExplorerTitle: 'Esploratore API',
    apiExplorerSubtitle: 'Il contract OpenAPI, navigabile',
    apiExplorerBody: 'Il contract OpenAPI completo della piattaforma — ogni rotta, schema e codice di risposta — esplorabile dal browser, servito attraverso questo nodo.',
    apiExplorerLink: 'Esplora API',
    kubeTitle: 'Compatibilità Kubernetes',
    kubeSubtitle: 'kubectl e i suoi strumenti, davanti all\'API nativa',
    kubeBody: 'TVirt KubeCompat proietta Pod, Deployment, Service, namespace, nodi ed eventi nel dialetto Kubernetes — in sola lettura, più la cancellazione dei Deployment. Ogni chiamata passa dall\'API nativa con i tuoi permessi: nel kubeconfig usa un app token come bearer.',
    kubeEndpoint: 'Endpoint per kubectl',
    kubeStatusActive: 'Attivo — Kubernetes {version}',
    kubeStatusMissing: 'Non installato su questo nodo (pacchetto trantor-tvirt-kubecompat)',
    kubeStatusDown: 'Installato ma non risponde — controlla l\'unità trantor-tvirt-kubecompat',
    kubeStatusChecking: 'Verifica in corso…'
  },

  // Licensing (round 2026-07-29): la pagina della licenza a sottoscrizione.
  licensing: {
    title: 'Licenza del cluster',
    subtitle: 'Licenza a sottoscrizione — al massimo una per cluster',
    absentTitle: 'Nessuna licenza registrata',
    absentBody:
      'Senza una licenza valida il cluster rifiuta di far partire nuove macchine virtuali (le VM accese restano accese). Registra il product key ricevuto con la sottoscrizione.',
    registerButton: 'Registra licenza',
    removeButton: 'Rimuovi licenza',
    state: {
      ok: 'Valida',
      limitExceeded: 'Limite superato',
      expired: 'Scaduta'
    },
    productKeyLabel: 'Product key',
    basisLabel: 'Base del limite',
    basis: {
      concurrentVms: 'VM accese contemporanee',
      physicalCores: 'Core fisici (totale cluster)'
    },
    limitLabel: 'Limite',
    usageLabel: 'Uso attuale',
    usageOf: '{used} di {limit}',
    usagePartial: 'somma parziale — {n} nodo/i senza topologia CPU osservata',
    usageUnknown: 'non osservabile al momento',
    expiresLabel: 'Scadenza',
    neverExpires: 'mai',
    registeredLabel: 'Registrata',
    gateNote:
      'Con licenza assente, scaduta o oltre il limite il cluster rifiuta di far partire NUOVE macchine virtuali. Le VM accese restano accese e i riavvii HA non ne risentono.',
    devStage:
      'Fase di sviluppo: il product key è accettato così com\'è insieme ai limiti dichiarati qui; la validazione con chiave firmata arriverà in una release futura.',
    dialogTitle: 'Registra licenza',
    dialogExpiresHelp: 'Vuoto = la licenza non scade mai',
    dialogSubmit: 'Registra',
    dialogError: 'Impossibile registrare la licenza.',
    removeTitle: 'Rimuovi licenza',
    removeBody:
      'Il cluster resterà SENZA LICENZA: le nuove macchine virtuali rifiuteranno di partire finché non ne registri una. Le VM accese restano accese.',
    removeConfirm: 'Rimuovi',
    removeError: 'Impossibile rimuovere la licenza.'
  },
  rbac: {
    tabRoles: 'Ruoli',
    tabGrants: 'Deleghe',
    rolesTitle: 'Ruoli',
    createRoleTitle: 'Crea ruolo',
    editRoleTitle: 'Modifica ruolo',
    nameHint: 'Minuscole, cifre e trattini; il prefisso tvirt- è riservato',
    systemChip: 'Sistema',
    permissions: 'Permessi',
    permissionsPlaceholder: 'Aggiungi permesso…',
    includedRoles: 'Ruoli inclusi',
    includedRolesPlaceholder: 'Aggiungi ruolo…',
    grantsTitle: 'Deleghe',
    createGrantTitle: 'Crea delega',
    principal: 'Principal',
    principalKind: 'Tipo di principal',
    principalUser: 'Utente',
    principalGroup: 'Gruppo',
    role: 'Ruolo',
    grantRight: 'Delega',
    grantRightChip: 'Può delegare',
    grantRightLabel: 'Con diritto di delega (il destinatario può a sua volta delegare questo ruolo)',
    effective: 'Effettiva',
    notEffective: 'Non effettiva',
    createdAt: 'Creata',
    scopeHint: '/path esatto, /path/* ricorsivo — le deleghe si ancorano a una radice montata (/* non è delegabile)'
  },
  groups: {
    title: 'Gruppi',
    createTitle: 'Crea gruppo',
    nameHint: 'Minuscole, cifre e trattini; unico nello scope',
    members: 'Membri',
    membersTitle: 'Membri',
    membersPlaceholder: 'Aggiungi utente…',
    noMembers: 'Nessun membro',
    realmMappings: 'Mappature realm',
    realmMappingsTitle: 'Mappature realm',
    realmMappingsHint:
      'A che cosa corrisponde questo gruppo in ciascun realm esterno. L’appartenenza si legge dalla directory al momento dell’accesso: un gruppo di directory che nessuno mappa qui viene ignorato, e l’annidamento non viene risolto — va appiattito lato directory.',
    realm: 'Realm',
    externalGroup: 'Gruppo esterno',
    addRealmMapping: 'Aggiungi mappatura',
    removeRealmMapping: 'Rimuovi mappatura',
    noRealmMappings: 'Nessuna mappatura realm',

    // Dialog di creazione a sezioni (round realm-wizard, 2026-08-03): Generale
    // più la superficie di mappatura coi realm esterni, con l'empty state
    // parlante.
    sectionGeneral: 'Generale',
    mappingsNoRealms: 'Nessun realm esterno configurato — la superficie di mappatura si sblocca appena esiste un realm.',
    mappingsNoRealmsLink: 'Apri il tab Realm e SSO',

    // L'obbligo MFA imposto dal gruppo (round #178 metà B): OR puro — un
    // gruppo può solo AGGIUNGERE l'obbligo, mai toglierlo.
    mfaRequired: 'Obbligo MFA',
    mfaRequiredChip: "Impone l'MFA",
    mfaNotRequiredChip: 'Nessun obbligo',
    mfaRequiredHint:
      "OR puro: un gruppo può AGGIUNGERE l'obbligo del secondo fattore ai suoi membri — anche alle identità esterne, via mappature realm — mai toglierlo. Vale dal login successivo: le sessioni aperte non vengono chiuse."
  },
  users: {
    title: 'Utenti',
    createTitle: 'Crea utente',
    editTitle: 'Modifica utente',
    username: 'Nome utente',
    usernameHint: 'Minuscole, cifre, punti e trattini',
    displayName: 'Nome visualizzato',
    enabled: 'Abilitato',
    state: 'Stato',
    enabledChip: 'Abilitato',
    disabledChip: 'Disabilitato',
    lockedChip: 'Bloccato',
    password: 'Password',
    passwordSetChip: 'Impostata',
    mustChangeChip: 'Da cambiare',
    initialPassword: 'Password iniziale',
    initialPasswordHint: "L'utente dovrà cambiarla al primo accesso",
    lastLogin: 'Ultimo accesso',
    neverLoggedIn: 'Mai entrato',
    setPasswordTitle: 'Imposta password',
    setPasswordHint: 'Sostituzione amministrativa — richiede la tua password (modalità sudo) e obbliga l\'utente a cambiarla al prossimo accesso.',
    operatorPassword: 'La tua password',
    unlock: 'Sblocca l\'account',
    revokeTitle: 'Revoca sessioni',
    revokeBody: 'Tutte le sessioni attive di questo utente vengono terminate immediatamente.',
    revokeIncludeTokens: 'Revoca anche gli app token',
    revokeConfirm: 'Revoca',

    // Dialog standard (round #156): dettagli completi, tab account/sicurezza.
    tabAccount: 'Account',
    tabSecurity: 'Sicurezza',
    usernameImmutableHint: "Il nome utente è l'identità dell'account — fissato alla creazione",
    groups: 'Gruppi',
    noGroups: 'Nessun gruppo',
    mustChangePassword: 'Cambio password obbligatorio',
    failedAttempts: 'Tentativi di accesso falliti',
    lockedUntil: 'Bloccato fino a',

    // Identity tabs (2026-08-03): Sistema > Utenti è diventata una sezione a
    // tab sul modello di /system/rbac — utenti, gruppi, realm esterni.
    tabUsers: 'Utenti',
    tabGroups: 'Gruppi',
    tabRealms: 'Realm e SSO',
    tabMfa: 'MFA',

    // Tab MFA (task #178): la vista del secondo fattore. Metà A — la tabella
    // per-utente su users.yaml; metà B (round contract, ratifiche 2026-08-04)
    // — il verdetto efficace con la provenienza, gli enrollment esterni con
    // lo stato orfano e i gruppi che impongono l'obbligo.
    mfa: {
      title: 'Secondo fattore (MFA)',
      methodsTitle: 'Metodi disponibili',
      methodsSubtitle: 'Ciò che questa installazione sa verificare come secondo fattore',
      methodsError: 'Impossibile caricare il registro dei metodi',
      builtinChip: 'Integrato',
      enrollableChip: 'Enrollment in TVirt',
      delegatedChip: 'Gestito esternamente',
      state: 'Stato',
      stateActive: 'Attivo',
      statePending: 'In attesa di enrollment',
      stateNone: 'Non arruolato',
      required: 'Obbligo MFA',
      requiredChip: 'Obbligatorio',
      notRequiredChip: 'Non obbligatorio',
      enrolledAt: 'Enrollment del',
      enrolledMethods: 'Metodi arruolati',
      noMethods: 'Nessun enrollment',
      codesRemaining: 'Codici di recupero rimasti',
      resetTitle: 'Resetta il secondo fattore',
      resetHint:
        "L'enrollment e tutti i codici di recupero vengono azzerati: al prossimo accesso l'utente rifarà l'enrollment da capo. L'obbligo MFA resta com'è — un amministratore non imposta mai un segreto, lo azzera soltanto.",
      resetConfirm: 'Resetta',
      resetError: 'Impossibile resettare il secondo fattore',
      empty: 'Ancora nessun utente gestito — gli account compaiono qui appena esistono, ciascuno col suo stato del secondo fattore.',

      // Metà B (round #178): provenienza del verdetto efficace, enrollment
      // esterni con lo stato orfano, gruppi che impongono l'obbligo.
      viaSelf: 'Personale',
      viaSelfHint: "Il contributo personale dell'account (il suo flag) — l'unica parte che la matita modifica.",
      viaGroupHint: "Obbligo ereditato da questo gruppo: togliere il flag personale non libera l'utente — si interviene sul gruppo, nel tab Gruppi.",
      subject: 'Identità',
      lastSeenAt: 'Ultima conferma',
      enrollmentPending: 'In sospeso',
      orphanChip: 'Orfano',
      orphanDirectoryMiss: "L'identità non è più nella directory",
      orphanRealmRemoved: 'Il realm è stato rimosso da TVirt',
      orphanedAt: 'Orfano dal',
      directoryUuid: 'UUID nella directory',
      externalTitle: 'Enrollment esterni',
      externalEmpty: "Ancora nessun enrollment esterno — nasce al primo login di un'identità di directory con l'obbligo MFA.",
      orphanHelp:
        "Un enrollment orfano non si cancella mai da solo: è materiale di sicurezza — resta per l'audit e, se l'identità ricompare nella directory, ritrova il suo fattore. L'unica uscita è il reset amministrativo.",
      visibilityHint: "Gli enrollment esterni risiedono alla radice del cluster: li vede solo chi ha user.read sull'intero albero.",
      orphanResetHint:
        "La riga orfana viene rimossa definitivamente: nessuno la reclama più. Se l'identità ricomparirà nella directory, rifarà l'enrollment da capo.",
      enforcingTitle: "Gruppi che impongono l'MFA",
      enforcingSubtitle:
        "L'ancora dell'obbligo per le identità esterne è il gruppo: le sue mappature realm dicono a quale popolazione di directory arriva. L'enrollment esterno nasce al primo login dell'utente obbligato.",
      enforcingEmpty: "Nessun gruppo impone l'MFA — l'obbligo si attiva sul gruppo, dal tab Gruppi.",
      enforcingError: "Impossibile caricare i gruppi che impongono l'MFA"
    }
  },
  realms: {
    title: 'Realm di autenticazione',
    createTitle: 'Crea realm',
    editTitle: 'Modifica realm',
    name: 'Realm',
    nameHint: 'Il dominio che gli utenti scrivono dopo la @ (example.com) — immutabile dopo la creazione',
    nameImmutableHint: 'Il nome del realm è stampato in ogni sessione e in ogni riga di audit che lo ha mai nominato — rinominarlo riscriverebbe la storia',
    kind: 'Tipo',
    kindLdap: 'LDAP / Active Directory',
    kindHint: 'OIDC e SAML arrivano coi prossimi round — oggi un realm è una directory LDAPv3',

    // Semi-wizard di creazione (round realm-wizard, 2026-08-03): le card dei
    // tipi del passo 1 — ldap vivo, oidc/saml card in roadmap (mai un form
    // senza un backend dietro).
    kindCategory: 'Tipo di realm',
    kindSelectHint: 'Seleziona il tipo di directory su cui questo realm autenticherà.',
    kindCard: {
      ldap: {
        name: 'LDAP / Active Directory',
        desc: 'Username e password verificati con un bind su una directory LDAPv3 — Active Directory, FreeIPA, OpenLDAP.'
      },
      oidc: {
        name: 'OpenID Connect',
        desc: 'Single sign-on dal browser delegato a un identity provider OpenID Connect — arriva coi prossimi round.'
      },
      saml: {
        name: 'SAML 2.0',
        desc: 'Single sign-on aziendale con asserzioni SAML 2.0 — arriva coi prossimi round.'
      }
    },
    tabBind: 'Bind',
    enabled: 'Abilitato',
    urls: 'Server della directory',
    urlsHint: 'Un URL ldap:// o ldaps:// per riga (1–8), provati in ordine — failover, mai bilanciamento',
    startTls: 'StartTLS',
    verifyCertificate: 'Verifica il certificato',
    cleartextWarning: 'Senza ldaps:// o StartTLS la password di bind attraversa la rete in chiaro — accettabile in laboratorio, mai in produzione.',
    noVerifyWarning: 'Una sessione TLS non verificata non autentica nessuno: disattiva la verifica solo verso una directory che non puoi sistemare.',
    caCertificate: 'Certificato CA (PEM)',
    caCertificateHint: 'Bundle CA opzionale fissato per l\'handshake TLS/StartTLS di questo realm; vuoto lo rimuove',
    caCertificatePinned: 'Fissato',
    connectTimeout: 'Timeout (secondi)',
    connectTimeoutHint: 'Scadenza per-URL di connessione e richiesta (1–60); allo scadere si prova l\'URL successivo',
    bindDn: 'Bind DN',
    bindDnHint: 'Il service account che esegue la ricerca dell\'utente; vuoto = search bind anonimo (rifiutato da AD)',
    anonymousBind: 'Search bind anonimo',
    bindCredential: 'Password di bind (keystore)',
    bindCredentialHint: 'Una voce usernamePassword del keystore con la password di bind — il segreto non vive mai sul realm',
    bindCredentialNone: 'Nessuna (bind anonimo)',
    bindCredentialEmpty: 'Nessuna voce usernamePassword nel keystore — creane una in Sistema > Credenziali per la password di bind.',
    baseDn: 'Base DN',
    baseDnHint: 'Il sottoalbero da cui parte la ricerca dell\'utente (DC=example,DC=com)',
    userFilter: 'Filtro utente',
    userFilterHint: '%u è sostituito dalla parte locale del login, con escape RFC 4515; le directory non-AD tipicamente vogliono (&(objectClass=inetOrgPerson)(uid=%u))',
    memberOfAttribute: 'Attributo memberOf',
    memberOfAttributeHint: 'Letto una volta sola, al login — i gruppi non mappati si ignorano, l\'annidamento non si risolve',
    uuidAttribute: 'Attributo UUID',
    uuidAttributeHint: 'L\'UUID immutabile nel realm (objectGUID su AD, entryUUID su OpenLDAP) — stampato nell\'audit accanto a utente@realm',
    displayNameAttribute: 'Attributo nome visualizzato',
    displayNameAttributeHint: 'Usato per salutare l\'utente; in mancanza si usa il nome di login',
    sudoHint: 'Le scritture sui realm sono sudo-protette: un realm è una porta d\'ingresso nella piattaforma',
    deleteBody: 'Le mappature gruppi del realm se ne vanno con lui; le sessioni che ha autenticato restano vive fino a scadenza — un realm cancellato semplicemente non ammette più nessuno.',
    tabConnection: 'Connessione',
    tabDirectory: 'Directory',
    testTitle: 'Prova connessione',
    testHint: 'Una sonda di configurazione, mai un\'autenticazione: si connette, fa il bind col service account e — con uno username — esegue il filtro utente.',
    testUsername: 'Nome utente (opzionale)',
    testUsernameHint: 'Una parte locale su cui eseguire il filtro utente — 1 corrispondenza è la risposta sana',
    testRun: 'Esegui prova',
    testError: 'Impossibile eseguire la sonda',
    testReachable: 'Directory raggiungibile, bind di servizio accettato',
    testUnreachable: 'Directory NON raggiungibile',
    matchedOne: 'Il filtro utente ha trovato esattamente 1 voce — la risposta sana.',
    matchedNone: 'Il filtro utente non ha trovato nessuna voce: il filtro o il base DN è sbagliato.',
    matchedMany: 'Il filtro utente ha trovato {count} voci: non è selettivo, e i login sarebbero ambigui (rifiutati).',
    mappingsPointer: 'A cosa corrisponde un gruppo esterno è una proprietà del gruppo TVirt (le sue mappature realm): il punto di verità vive nel tab Gruppi.',
    mappingsPointerLink: 'Apri il tab Gruppi',
    rootHomeHint: 'Le identità di un realm esterno vivono nella radice del cluster, mai in un vDC: i permessi si assegnano dove servono, con le deleghe — sul gruppo o direttamente sull\'utente, esattamente come per i nativi.'
  },
  credentials: {
    title: 'Credenziali',
    createTitle: 'Crea credenziale',
    editTitle: 'Modifica credenziale',
    kind: 'Tipo',
    kindUsernamePassword: 'Username e password',
    kindIscsiChap: 'CHAP (iSCSI)',
    kindNvmeDhchap: 'DH-HMAC-CHAP (NVMe)',
    kindCephx: 'cephx (identità Ceph)',
    kindHintUsernamePassword: "Immutabile. Per gli storage CIFS/SMB — l'account di mount il cui segreto il worker usa al momento del mount.",
    kindHintIscsiChap: "Immutabile. Per l'autenticazione CHAP iSCSI — username = nome CHAP, secret = segreto CHAP.",
    kindHintNvmeDhchap: "Immutabile. Per il DH-HMAC-CHAP di NVMe over Fabrics — secret = il blob DHHC-1:xx:…; lo username è solo un'etichetta.",
    kindHintCephx: 'Immutabile. Per i client Ceph — username = nome client (client.…), secret = la chiave base64.',
    kindImmutableHint: 'Il tipo è immutabile dopo la creazione — cambiarlo significa una nuova credenziale.',
    username: 'Nome utente',
    usernameChap: 'Nome CHAP',
    usernameNvme: 'Etichetta',
    usernameCephx: 'Nome client',
    usernameHint: 'Account opzionale a cui appartiene il segreto',
    usernameHintChap: "Il nome CHAP con cui l'initiator si autentica",
    usernameHintNvme: "Non usata dal trasporto — un'etichetta libera per i tuoi appunti",
    usernameHintCephx: 'Il nome del client cephx — il prefisso client. è tollerato',
    secret: 'Segreto',
    secretHint: 'Solo scrittura: usato dai consumatori, mai più mostrato.',
    secretHintNvmeDhchap: 'Solo scrittura: il segreto DH-HMAC-CHAP host nella forma canonica DHHC-1:xx:… — usato dai consumatori, mai più mostrato.',
    secretHintCephx: 'Solo scrittura: la chiave cephx base64 — usata dai consumatori, mai più mostrata.',
    secretEditHint: 'Lascia vuoto per non cambiare il segreto — compilarlo lo ruota.',
    createdAt: 'Creata',
    inUseError: "'{name}' è referenziata dalla configurazione di uno storage — prima scollegala lì.",
    nameHint: 'Minuscole, cifre e trattini (DNS-1123); unica per scope',

    // Dialog di sola rotazione (round #156): il segreto non ha mai la matita inline.
    rotateTitle: 'Ruota segreto',
    rotateHint: 'Solo scrittura: il nuovo segreto sostituisce quello memorizzato — i consumatori lo useranno al prossimo mount.',

    // Round #159 (PKI): i tab Certificati e CA.
    tabSecrets: 'Segreti',
    tabCertificates: 'Certificati',
    tabAuthorities: 'Autorità di certificazione',
    certificatesTitle: 'Certificati',
    authoritiesTitle: 'Autorità di certificazione',
    caCreateTitle: 'Nuova CA',
    certCreateTitle: 'Nuovo certificato',
    caMode: 'Origine',
    caModeGenerate: 'Genera con TVirt',
    caModeImport: 'Importa (chiave + certificato)',
    certMode: 'Origine',
    certModeIssue: 'Emetti da una CA del keystore',
    certModeImport: 'Importa (chiave + certificato)',
    issuingCa: 'CA emittente',
    issuingCaHint: 'Il certificato nasce nello scope della CA, col parent registrato',
    noCasHint: 'Nessuna CA nel keystore — prima generane o importane una nel tab CA',
    commonName: 'Common name (CN)',
    commonNameCaHint: 'Il subject della CA: lettere, cifre, spazio, punto, underscore, trattino',
    commonNameCertHint: 'OpenVPN identifica i client dal CN — tienilo unico per CA',
    days: 'Validità (giorni)',
    daysCaHint: '1–7300 giorni; 3650 (10 anni) è il default',
    daysCertHint: '1–3650 giorni; 365 è il default',
    keyType: 'Tipo di chiave',
    keyTypeHint: 'EC P-256 è il default; RSA per consumatori legacy',
    usage: 'Uso',
    usageClient: 'Client (clientAuth)',
    usageServer: 'Server (serverAuth)',
    usageHint: "L'extended key usage per cui il certificato viene emesso",
    san: 'Nomi alternativi (SAN)',
    sanHint: 'Separati da virgola; gli IP letterali diventano voci IP:, il resto DNS:',
    keyPem: 'Chiave privata (PEM)',
    keyPemHint: 'Solo scrittura: custodita nel keystore, mai più mostrata',
    certPem: 'Certificato (PEM)',
    certPemHint: 'Il certificato pubblico che si accoppia alla chiave',
    certPemCaHint: 'Il certificato CA che si accoppia alla chiave (basicConstraints CA)',
    subject: 'Subject',
    issuer: 'Emittente',
    parentCa: 'CA emittente',
    parentCaImported: 'Importato — emesso fuori da TVirt',
    serial: 'Seriale',
    fingerprint: 'Fingerprint (SHA-256)',
    validFrom: 'Valido dal',
    validUntil: 'Valido fino al',
    expiry: 'Scadenza',
    expired: 'Scaduto',
    expiresInDays: 'Scade tra {days} g',
    revoked: 'Revocato',
    revokedAtLabel: 'Revocato il',
    revokeTitle: 'Revoca certificato',
    revokeAction: 'Revoca',
    revokeWarning: 'La revoca è definitiva: il seriale entra nella CRL della CA emittente e ogni consumatore che rifiuta i certificati revocati (OpenVPN in primis) respingerà questo. Non esiste un-revoke.',
    exportPem: 'Esporta PEM',
    exportChain: 'Esporta bundle catena',
    exportCrl: 'Esporta CRL',
    materialImmutableHint: 'Il materiale X.509 è immutabile — riemetti o reimporta',
    caInUseError: "'{name}' ha ancora certificati emessi vivi (o uno storage la referenzia) — prima revocali o cancellali."
  },
  proximity: {
    title: 'TVirt Proximity',
    subtitle: 'Piazzamento, disponibilità e affinità — dove girano le VM del cluster e se devono essere accese',
    loadError: 'Il pannello Proximity richiede lo store di cluster — lo stato della flotta ora non è raggiungibile',
    config: {
      title: 'Configurazione',
      subtitle: 'Politica di piazzamento a livello di cluster',
      save: 'Salva',
      saveError: 'La configurazione è stata rifiutata',
      strategy: 'Strategia di selezione',
      strategy_leastUsedResources: 'Meno risorse usate',
      strategy_leastVms: 'Meno VM',
      strategy_random: 'Casuale',
      strategyLeastUsedResourcesHint: 'Tra i candidati validi, il nodo con più RAM usabile residua (totale osservato − riserva host − VM accese), impegno vCPU come spareggio. Deterministica a parità di dati.',
      strategyLeastVmsHint: 'Tra i candidati validi, il nodo con meno VM accese; i pareggi si risolvono per nome. Deterministica a parità di dati.',
      strategyRandomHint: 'Uniformemente casuale tra i candidati validi.',
      liveness: 'Soglia di vitalità dei nodi (secondi)',
      livenessHint: 'Un membro silente oltre la soglia è irraggiungibile: escluso dal piazzamento e — se non in manutenzione — considerato morto dal decider HA, che lo recinta e riavvia altrove le sue VM HA.',
      decider: 'Decider HA',
      deciderNone: 'non ancora eletto'
    },
    nodesTitle: 'Nodi',
    nodesSubtitle: 'Vitalità e capacità empirica — RAM usabile = totale − base − impegnata',
    nodeCol: 'Nodo',
    stateCol: 'Stato',
    ramCol: 'RAM (totale − base − impegnata)',
    usableCol: 'Usabile',
    vmsCol: 'Carico',
    neverSeen: 'mai visto',
    freshChip: 'raggiungibile',
    staleChip: 'irraggiungibile',
    attestedChip: 'attestato spento',
    ramFormula: 'totale − base − impegnata',
    haTitle: 'Alta disponibilità',
    haSubtitle: 'Le VM con flag HA sono "sempre accese": TVirt le riavvia dopo ogni stop non richiesto — stesso nodo con backoff, un altro nodo quando il loro muore (solo storage condiviso).',
    haNone: 'Nessuna VM ha ancora il flag HA — abilitalo qui o nel dettaglio della VM.',
    haVmCol: 'Macchina virtuale',
    haStateCol: 'Stato',
    haRestartsCol: 'Riavvii HA',
    haLastEventCol: 'Ultimo evento',
    haToggleCol: 'HA',
    haCrashLoop: 'crash loop',
    haToggleError: 'La modifica HA è stata rifiutata',
    rules: {
      title: 'Regole di affinità',
      createTitle: 'Nuova regola di prossimità',
      editTitle: 'Modifica regola',
      nameHint: 'Minuscole, cifre e trattini (etichetta DNS)',
      vms: 'Macchine virtuali',
      vmsHint: 'Almeno due VM — la regola vincola dove girano le une rispetto alle altre',
      type: 'Tipo',
      affinity: 'Affinità (insieme)',
      antiAffinity: 'Anti-affinità (separate)',
      typeAffinityHint: 'Le VM devono girare sullo STESSO nodo.',
      typeAntiAffinityHint: 'Le VM devono girare su nodi DIVERSI — la classica coppia HA.',
      mode: 'Modalità',
      hard: 'hard',
      soft: 'soft',
      modeHardHint: "Le violazioni rifiutano l'operazione (avvio, migrazione, failover) con un errore parlante.",
      modeSoftHint: 'Preferenza, mai bloccante: un piazzamento in violazione procede con un avviso visibile.',
      satisfied: 'Piazzamento',
      satisfiedYes: 'rispettato',
      satisfiedNo: 'violato',

      // Dialog standard (round #156): le violazioni hanno la loro riga, non un tooltip.
      violations: 'Violazioni',
      noViolations: 'Nessuna'
    },
    events: {
      title: 'Eventi HA',
      subtitle: 'Il giornale della disponibilità — riavvii, fencing, failover e i salti onesti',
      none: 'Ancora nulla.',
      when: 'Quando',
      kind: 'Evento',
      target: 'Oggetto',
      detail: 'Dettaglio',
      kindRestarted: 'riavvio HA',
      kindGaveUp: 'resa (crash loop)',
      kindNodeAttested: 'nodo recintato',
      kindFailedOver: 'failover',
      kindNoCandidate: 'nessun candidato',
      kindStorageLocalSkip: 'saltata (storage locale)',
      kindZombieFenced: 'zombie terminato'
    }
  }
}
